import React, { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom";

/* ==========================================================================
   SCORE / EVENT CONFIGURATION
   ========================================================================== */

const SCORE_TYPES = {
  try: { label: "Try", points: 5 },
  conversion: { label: "Conversion", points: 2 },
  penalty: { label: "Penalty", points: 3 },
  drop_goal: { label: "Drop Goal", points: 3 },
  penalty_try: { label: "Penalty Try", points: 7 },
  yellow_card: { label: "Yellow Card", points: 0 },
  red_card: { label: "Red Card", points: 0 },
  conversion_missed: { label: "Conversion Missed", points: 0 },
  penalty_missed: { label: "Penalty Missed", points: 0 },
};

const TYPE_NO_PLAYER = new Set(["penalty_try"]);

const EVENT_SECTION_ORDER = [
  { key: "try", label: "Try", types: ["try", "penalty_try"] },
  { key: "conversion", label: "Conversion", types: ["conversion"] },
  { key: "penalty", label: "Penalty", types: ["penalty"] },
  { key: "drop_goal", label: "Drop Goal", types: ["drop_goal"] },
  { key: "yellow_card", label: "Yellow Card", types: ["yellow_card"] },
  { key: "red_card", label: "Red Card", types: ["red_card"] },
  {
    key: "conversion_missed",
    label: "Conversion Missed",
    types: ["conversion_missed"],
  },
  {
    key: "penalty_missed",
    label: "Penalty Missed",
    types: ["penalty_missed"],
  },
];

const EXPORT_SECTIONS = [
  { label: "Tries", types: ["try", "penalty_try"] },
  { label: "Conversions", types: ["conversion"] },
  { label: "Penalties", types: ["penalty"] },
  { label: "Drop Goals", types: ["drop_goal"] },
  { label: "Yellow Cards", types: ["yellow_card"] },
  { label: "Red Cards", types: ["red_card"] },
];

/* ==========================================================================
   SPONSOR CONFIGURATION
   ========================================================================== */

const SPONSOR = {
  enabled: true,
  name: "Hamptons Group",
  imgSrc: "/sponsor.png",
  link: "https://hamptonsgroup.uk/",
  alt: "Hamptons Group — Official Sponsor",
};

/* ==========================================================================
   SMALL UTILITY HELPERS
   ========================================================================== */

const fmtSec = (sec) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatClock = (s) => {
  const sec = Math.max(0, Math.floor(s));
  const mm = Math.floor(sec / 60)
    .toString()
    .padStart(2, "0");
  const ss = (sec % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
};

const formatHumanDate = (str) => {
  if (!str) return "";

  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return str;

  const [, d, mo, y] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  if (isNaN(dt)) return str;

  return dt.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const parseNames = (text) =>
  text
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const shortPlayerName = (name) => {
  if (!name) return "Unknown";

  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];

  const first = parts[0];
  const last = parts[parts.length - 1];
  return `${first[0]}. ${last}`;
};

const isLiveSlot = (index, activeTeamSize) => index < activeTeamSize;

const formatEventTime = (seconds, half, halfLength) => {
  const minute = Math.max(1, Math.ceil(seconds / 60));
  const halfEnd = halfLength * 60;
  const fullTimeEnd = halfLength * 120;

  if (half === 1) {
    if (seconds <= halfEnd) return `${minute}′`;
    const extra = Math.ceil((seconds - halfEnd) / 60);
    return `${halfLength}+${extra}′`;
  }

  if (half === 2) {
    if (seconds <= fullTimeEnd) return `${minute}′`;
    const extra = Math.ceil((seconds - fullTimeEnd) / 60);
    return `${halfLength * 2}+${extra}′`;
  }

  return `${minute}′`;
};

const getStoredJson = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
};

const buildDefaultTimer = () => ({
  on: false,
  elapsedSec: 0,
  lastStartTs: null,
});

const loadSquad = (key) => {
  try {
    const saved = localStorage.getItem(key);
    const parsed = saved ? JSON.parse(saved) : [];

    return parsed.map((item, index) => {
      if (typeof item === "string") {
        return { number: index + 1, name: item, injured: false };
      }

      return {
        ...item,
        injured: item.injured ?? false,
      };
    });
  } catch {
    return [];
  }
};

const reconcileTeamTimers = (
  squad,
  prevTeamTimers,
  now,
  shouldRun,
  activeTeamSize
) => {
  const next = {};

  (squad || []).forEach((player, index) => {
    const existing = prevTeamTimers?.[player.name] || buildDefaultTimer();
    const shouldBeOn = shouldRun && isLiveSlot(index, activeTeamSize);

    if (shouldBeOn) {
      next[player.name] = existing.on
        ? existing
        : {
            ...existing,
            on: true,
            lastStartTs: now,
          };
      return;
    }

    if (existing.on && existing.lastStartTs) {
      const add = Math.floor((now - existing.lastStartTs) / 1000);
      next[player.name] = {
        ...existing,
        on: false,
        elapsedSec: (existing.elapsedSec || 0) + add,
        lastStartTs: null,
      };
      return;
    }

    next[player.name] = {
      ...existing,
      on: false,
      lastStartTs: null,
    };
  });

  return next;
};

/* ==========================================================================
   MAIN COMPONENT
   ========================================================================== */

export default function RugbyScoreboardApp() {
  /* ------------------------------------------------------------------------
     REFS
     ------------------------------------------------------------------------ */

  const scoreboardRef = useRef(null);
  const exportCardRef = useRef(null);
  const playingSummaryRef = useRef(null);

  /* ------------------------------------------------------------------------
     CORE MATCH STATE
     ------------------------------------------------------------------------ */

  const [teamA, setTeamA] = useState(
    () => localStorage.getItem("teamA") || "Home"
  );
  const [teamB, setTeamB] = useState(
    () => localStorage.getItem("teamB") || "Away"
  );

  const [scoreA, setScoreA] = useState(
    () => Number(localStorage.getItem("scoreA")) || 0
  );
  const [scoreB, setScoreB] = useState(
    () => Number(localStorage.getItem("scoreB")) || 0
  );

  const [events, setEvents] = useState(() => getStoredJson("events", []));

  const [seconds, setSeconds] = useState(
    () => Number(localStorage.getItem("seconds")) || 0
  );
  const [running, setRunning] = useState(
    () => localStorage.getItem("running") === "true"
  );
  const [currentHalf, setCurrentHalf] = useState(
    () => Number(localStorage.getItem("currentHalf")) || 0
  );
  const [halfLength, setHalfLength] = useState(
    () => Number(localStorage.getItem("halfLength")) || 40
  );
  const [startTimestamp, setStartTimestamp] = useState(
    () => Number(localStorage.getItem("startTimestamp")) || null
  );

  const [matchDate, setMatchDate] = useState(
    () => localStorage.getItem("matchDate") || ""
  );
  const [koTime, setKoTime] = useState(
    () => localStorage.getItem("koTime") || ""
  );
  const [halfTimeScore, setHalfTimeScore] = useState(() =>
    getStoredJson("halfTimeScore", null)
  );

  /* ------------------------------------------------------------------------
     YELLOW CARD STATE
     ------------------------------------------------------------------------ */

  const [ycDuration, setYcDuration] = useState(
    () => Number(localStorage.getItem("ycDuration")) || 10
  );
  const [ycTimers, setYcTimers] = useState(() => getStoredJson("ycTimers", []));

  /* ------------------------------------------------------------------------
     SCORING MODAL STATE
     ------------------------------------------------------------------------ */

  const [scoringFor, setScoringFor] = useState(null);
  const [scoringType, setScoringType] = useState("try");
  const [scoringPlayer, setScoringPlayer] = useState("");
  const [selectedSquadName, setSelectedSquadName] = useState("");

  /* ------------------------------------------------------------------------
     TEAM SHEETS / SQUADS STATE
     ------------------------------------------------------------------------ */

  const [bulkA, setBulkA] = useState("");
  const [bulkB, setBulkB] = useState("");

  const [teamASquad, setTeamASquad] = useState(() => loadSquad("teamASquad"));
  const [teamBSquad, setTeamBSquad] = useState(() => loadSquad("teamBSquad"));

  const [showClearSquadConfirm, setShowClearSquadConfirm] = useState(null);
  const [playerToRemove, setPlayerToRemove] = useState(null);
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [activeManagementTeam, setActiveManagementTeam] = useState(null);

  /* ------------------------------------------------------------------------
     TEAM NAME EDITING STATE
     ------------------------------------------------------------------------ */

  const [editingTeam, setEditingTeam] = useState(null);
  const [editingTeamName, setEditingTeamName] = useState("");

  /* ------------------------------------------------------------------------
     EVENT EDITING STATE
     ------------------------------------------------------------------------ */

  const [editingEventId, setEditingEventId] = useState(null);
  const [editingPlayerName, setEditingPlayerName] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedGroupedEvents, setSelectedGroupedEvents] = useState(null);

  /* ------------------------------------------------------------------------
     PLAYER TIMERS STATE
     ------------------------------------------------------------------------ */

  const [playerTimers, setPlayerTimers] = useState(() =>
    getStoredJson("playerTimers", { A: {}, B: {} })
  );
  const [activeTeamSize, setActiveTeamSize] = useState(
    () => Number(localStorage.getItem("activeTeamSize")) || 15
  );
  const [showPlayingSummary, setShowPlayingSummary] = useState(false);

  /* ------------------------------------------------------------------------
     EXPORT / SPLASH / CONFIRMATION STATE
     ------------------------------------------------------------------------ */

  const [isExporting, setIsExporting] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  /* ------------------------------------------------------------------------
     DERIVED VALUES
     ------------------------------------------------------------------------ */

  const halfEndTime = currentHalf === 1 ? halfLength * 60 : halfLength * 120;
  const isOvertime = currentHalf > 0 && seconds > halfEndTime;

  /* ------------------------------------------------------------------------
     EVENT / EXPORT GROUPING HELPERS
     ------------------------------------------------------------------------ */

  const exportGroupedItems = (team, typeKeys) => {
    const filtered = events.filter(
      (e) => e.team === team && typeKeys.includes(e.type)
    );
    if (!filtered.length) return [];

    const grouped = {};

    filtered.forEach((e) => {
      let player = (e.player || "Unknown").trim();

      if (e.type === "penalty_try") {
        player = "Penalty Try";
      }

      if (!grouped[player]) {
        grouped[player] = [];
      }

      grouped[player].push({
        sortValue: e.timeSec ?? (e.timeMin || 0) * 60,
        label: e.timeLabel || `${e.timeMin}′`,
      });
    });

    return Object.entries(grouped).map(([player, items]) => ({
      player,
      items: items.sort((a, b) => a.sortValue - b.sortValue),
    }));
  };

  const groupedEventEntries = (team, typeKeys) => {
    const filtered = events.filter(
      (e) => e.team === team && typeKeys.includes(e.type)
    );
    if (!filtered.length) return [];

    const grouped = new Map();

    filtered.forEach((e) => {
      let player = (e.player || "Unknown").trim();

      if (e.type === "penalty_try") {
        player = "Penalty Try";
      }

      const key = `${e.type}__${player}`;

      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          player,
          type: e.type,
          typeLabel: e.typeLabel,
          events: [],
        });
      }

      grouped.get(key).events.push(e);
    });

    return Array.from(grouped.values())
      .map((group) => ({
        ...group,
        events: group.events.sort(
          (a, b) => (a.timeSec ?? a.timeMin * 60) - (b.timeSec ?? b.timeMin * 60)
        ),
      }))
      .sort((a, b) => {
        const aFirst = a.events[0]?.timeSec ?? (a.events[0]?.timeMin ?? 999) * 60;
        const bFirst = b.events[0]?.timeSec ?? (b.events[0]?.timeMin ?? 999) * 60;
        return aFirst - bFirst;
      });
  };

  /* ------------------------------------------------------------------------
     SQUAD MANAGEMENT HELPERS
     ------------------------------------------------------------------------ */

  const addBulkToSquad = (team) => {
    const raw = team === "A" ? bulkA : bulkB;
    const names = parseNames(raw);
    if (!names.length) return;

    if (team === "A") {
      setTeamASquad((prev) => {
        const existingNames = new Set(prev.map((p) => p.name));
        const next = [...prev];

        names.forEach((name) => {
          if (!existingNames.has(name)) {
            next.push({
              number: next.length + 1,
              name,
              injured: false,
            });
          }
        });

        return next;
      });
      setBulkA("");
      return;
    }

    setTeamBSquad((prev) => {
      const existingNames = new Set(prev.map((p) => p.name));
      const next = [...prev];

      names.forEach((name) => {
        if (!existingNames.has(name)) {
          next.push({
            number: next.length + 1,
            name,
            injured: false,
          });
        }
      });

      return next;
    });
    setBulkB("");
  };

  const swapPlayerPosition = (team, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    if (team === "A") {
      setTeamASquad((prev) => {
        const next = [...prev];
        [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
        return next;
      });
      return;
    }

    setTeamBSquad((prev) => {
      const next = [...prev];
      [next[fromIndex], next[toIndex]] = [next[toIndex], next[fromIndex]];
      return next;
    });
  };

  const handleSwapSelect = (team, index) => {
    if (!selectedSwap) {
      setSelectedSwap({ team, index });
      return;
    }

    if (selectedSwap.team !== team) {
      setSelectedSwap({ team, index });
      return;
    }

    if (selectedSwap.index === index) {
      setSelectedSwap(null);
      return;
    }

    swapPlayerPosition(team, selectedSwap.index, index);
    setSelectedSwap(null);
  };

  const clearSquad = (team) => {
    if (team === "A") setTeamASquad([]);
    else setTeamBSquad([]);
  };

  const removeFromSquad = (team, playerNumber) => {
    if (team === "A") {
      setTeamASquad((prev) => prev.filter((p) => p.number !== playerNumber));
      return;
    }

    setTeamBSquad((prev) => prev.filter((p) => p.number !== playerNumber));
  };

  const togglePlayerInjured = (team, playerNumber) => {
    if (team === "A") {
      setTeamASquad((prev) =>
        prev.map((player) =>
          player.number === playerNumber
            ? { ...player, injured: !player.injured }
            : player
        )
      );
      return;
    }

    setTeamBSquad((prev) =>
      prev.map((player) =>
        player.number === playerNumber
          ? { ...player, injured: !player.injured }
          : player
      )
    );
  };

  const pickSquadName = (player) => {
    setSelectedSquadName(player.name);
    setScoringPlayer(player.name);
  };

  const updatePlayerNumber = (team, playerName, newNumber) => {
  const cleaned = String(newNumber).replace(/\D/g, "");

  if (team === "A") {
    setTeamASquad((prev) =>
      prev.map((player) =>
        player.name === playerName
          ? { ...player, number: cleaned === "" ? "" : Number(cleaned) }
          : player
      )
    );
  } else {
    setTeamBSquad((prev) =>
      prev.map((player) =>
        player.name === playerName
          ? { ...player, number: cleaned === "" ? "" : Number(cleaned) }
          : player
      )
    );
  }
};


  /* ------------------------------------------------------------------------
     MATCH CLOCK / HALF CONTROLS
     ------------------------------------------------------------------------ */

  const resetScoringState = () => {
    setScoringFor(null);
    setScoringType("try");
    setScoringPlayer("");
    setSelectedSquadName("");
  };

  const openScoring = (team) => {
    setScoringFor(team);
    setScoringType("try");
    setScoringPlayer("");
    setSelectedSquadName("");
  };

  const startHalf = (half) => {
    setCurrentHalf(half);
    setStartTimestamp(Date.now() - seconds * 1000);
    setRunning(true);
  };

  const togglePause = () => {
    if (running) {
      setRunning(false);
      return;
    }

    setStartTimestamp(Date.now() - seconds * 1000);
    setRunning(true);
  };

  const endHalf = () => {
    const endTime = currentHalf === 1 ? halfLength * 60 : halfLength * 120;
    const overrun = Math.max(0, seconds - endTime);

    if (overrun > 0) {
      setYcTimers((prev) =>
        prev.map((t) => ({
          ...t,
          issuedAtSeconds: t.issuedAtSeconds - overrun,
        }))
      );
    }

    setSeconds(endTime);
    setRunning(false);

    if (currentHalf === 1) {
      setHalfTimeScore({ a: scoreA, b: scoreB });
    }
  };

  /* ------------------------------------------------------------------------
     TIMER BUTTON STYLES
     ------------------------------------------------------------------------ */

  const timerButtonBase = {
    minWidth: 140,
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
    transition: "all 0.15s ease",
    boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
  };

  const startHalfButtonStyle = {
    ...timerButtonBase,
    background: "linear-gradient(135deg, #2e7d32, #43a047)",
    color: "#fff",
  };

  const endHalfButtonStyle = {
    ...timerButtonBase,
    background: "linear-gradient(135deg, #c62828, #e53935)",
    color: "#fff",
  };

  const pauseResumeButtonStyle = {
    ...timerButtonBase,
    background: running
      ? "linear-gradient(135deg, #ef6c00, #fb8c00)"
      : "linear-gradient(135deg, #1565c0, #1e88e5)",
    color: "#fff",
  };

  const timerButtonRowStyle = {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
  };

  /* ------------------------------------------------------------------------
     YELLOW CARD HELPERS
     ------------------------------------------------------------------------ */

  const getYcRemaining = (t) =>
    Math.max(0, t.durationSec - (seconds - t.issuedAtSeconds));

  const clearYcTimer = (id) => {
    setYcTimers((arr) => arr.filter((t) => t.id !== id));
  };

  /* ------------------------------------------------------------------------
     EVENT CREATION / EDIT / DELETE
     ------------------------------------------------------------------------ */

  const commitScoreDirect = (typeKey, playerInput = "") => {
    if (!scoringFor || !typeKey) return;

    const pts = SCORE_TYPES[typeKey]?.points ?? 0;
    const playerName =
      typeKey === "penalty_try"
        ? "*Penalty*"
        : (playerInput && playerInput.trim()) || "Unknown";

    const eventId = Math.random().toString(36).slice(2, 9);

    const event = {
      id: eventId,
      team: scoringFor,
      type: typeKey,
      typeLabel: SCORE_TYPES[typeKey].label,
      player: playerName,
      timeMin: Math.max(1, Math.ceil(seconds / 60)),
      timeLabel: formatEventTime(seconds, currentHalf, halfLength),
      half: currentHalf,
      timeSec: seconds,
    };

    setEvents((evs) => [...evs, event]);

    if (scoringFor === "A") setScoreA((s) => s + pts);
    else setScoreB((s) => s + pts);

    if (typeKey === "yellow_card") {
      const yc = {
        id: Math.random().toString(36).slice(2, 9),
        eventId,
        team: scoringFor,
        player: playerName,
        issuedAtSeconds: seconds,
        durationSec: (ycDuration ?? 10) * 60,
      };
      setYcTimers((arr) => [...arr, yc]);
    }

    resetScoringState();
  };

  const undoEvent = (id) => {
    const ev = events.find((e) => e.id === id);
    if (!ev) return;

    setEvents((prev) => prev.filter((e) => e.id !== id));

    if (ev.team === "A") setScoreA((s) => s - SCORE_TYPES[ev.type].points);
    else setScoreB((s) => s - SCORE_TYPES[ev.type].points);

    if (ev.type === "yellow_card") {
      setYcTimers((prev) =>
        prev.filter(
          (t) =>
            !(
              t.team === ev.team &&
              t.player === ev.player &&
              t.issuedAtSeconds === (Math.max(1, ev.timeMin) - 1) * 60
            )
        )
      );
    }
  };

  const saveEditEvent = () => {
    if (!editingEventId) return;

    setEvents((prev) =>
      prev.map((e) =>
        e.id === editingEventId
          ? { ...e, player: editingPlayerName.trim() || "Unknown" }
          : e
      )
    );

    setYcTimers((prev) =>
      prev.map((t) =>
        t.eventId === editingEventId
          ? { ...t, player: editingPlayerName.trim() || "Unknown" }
          : t
      )
    );

    setEditingEventId(null);
    setEditingPlayerName("");
  };

  const cancelEditEvent = () => {
    setEditingEventId(null);
    setEditingPlayerName("");
  };

  /* ------------------------------------------------------------------------
     PLAYER TIMER HELPERS
     ------------------------------------------------------------------------ */

  const livePlayerSec = (p) => {
    if (!p) return 0;

    let sec = p.elapsedSec || 0;
    if (p.on && p.lastStartTs && running) {
      sec += Math.floor((Date.now() - p.lastStartTs) / 1000);
    }

    return sec;
  };

  const getPlayerPercentage = (sec) => {
    const totalPossibleSeconds = halfLength * 60 * 2;
    if (!totalPossibleSeconds) return 0;
    return (sec / totalPossibleSeconds) * 100;
  };

  const getSquadRowTiming = (team, player, index) => {
    const timer = playerTimers?.[team]?.[player.name] || buildDefaultTimer();
    const sec = livePlayerSec(timer);
    const pct = getPlayerPercentage(sec);

    return {
      sec,
      time: fmtSec(sec),
      pct,
      status: player.injured
        ? "Injured"
        : index < activeTeamSize
        ? "On Field"
        : "Sub",
    };
  };

  const getPlayingSummary = (team) => {
    const squad = team === "A" ? teamASquad : teamBSquad;

    return (squad || []).map((player, index) => {
      const timer = playerTimers?.[team]?.[player.name] || buildDefaultTimer();
      const sec = livePlayerSec(timer);
      const pct = getPlayerPercentage(sec);

      return {
        number: player.number,
        name: player.name,
        time: fmtSec(sec),
        percentage: pct,
        onFieldSlot: index < activeTeamSize,
        injured: player.injured ?? false,
      };
    });
  };

  const resetAllPlayerTimers = () => {
    setPlayerTimers({ A: {}, B: {} });
  };

  /* ------------------------------------------------------------------------
     EXPORT / RESET HELPERS
     ------------------------------------------------------------------------ */

  const exportImage = async () => {
    if (!exportCardRef.current) return;

    setIsExporting(true);
    await new Promise((r) => setTimeout(r, 120));

    const canvas = await html2canvas(exportCardRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
    });

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${teamA}_vs_${teamB}_Result.png`;
    link.click();

    setIsExporting(false);
  };

  const exportPlayingSummaryImage = async () => {
    if (!playingSummaryRef.current) return;

    const canvas = await html2canvas(playingSummaryRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
    });

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${teamA}_vs_${teamB}_Playing_Summary.png`;
    link.click();
  };

  const newMatch = () => {
    setScoreA(0);
    setScoreB(0);
    setEvents([]);
    setSeconds(0);
    setRunning(false);
    setCurrentHalf(0);

    resetScoringState();

    setYcTimers([]);
    setHalfTimeScore(null);
    setMatchDate("");
    setKoTime("");

    setTeamA("Home");
    setTeamB("Away");

    resetAllPlayerTimers();
    localStorage.clear();
  };

  /* ------------------------------------------------------------------------
     RENDER HELPERS
     ------------------------------------------------------------------------ */

  const renderSquadRows = (team, squad) => {
    const onField = squad.slice(0, activeTeamSize);
    const subs = squad.slice(activeTeamSize);

    const renderPlayerRow = (player, actualIndex) => {
      const rowTiming = getSquadRowTiming(team, player, actualIndex);

      return (
        <div
          key={`${team}-${actualIndex}-${player.name}`}
          style={{
            display: "grid",
            gridTemplateColumns: "20px minmax(0, 1fr)",
            gap: 4,
            alignItems: "stretch",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: 13,
              color: "#111827",
              minHeight: 44,
            }}
          >
            {actualIndex + 1}.
          </div>

          <div
            onClick={() => handleSwapSelect(team, actualIndex)}
            style={{
              border:
                selectedSwap?.team === team && selectedSwap?.index === actualIndex
                  ? "2px solid #2563eb"
                  : "1px solid #94a3b8",
              borderRadius: 10,
              padding: "6px 8px",
              background:
                selectedSwap?.team === team && selectedSwap?.index === actualIndex
                  ? "#dbeafe"
                  : actualIndex < activeTeamSize
                  ? "#f0fdf4"
                  : "#ffffff",
              fontSize: 12,
              cursor: "pointer",
              minWidth: 0,
              boxShadow:
                selectedSwap?.team === team && selectedSwap?.index === actualIndex
                  ? "0 0 0 1px rgba(37,99,235,0.15)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                fontWeight: 800,
                fontSize: 12,
                lineHeight: 1.2,
                marginBottom: 3,
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {player.name}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 6,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: player.injured
                    ? "#b91c1c"
                    : actualIndex < activeTeamSize
                    ? "#166534"
                    : "#6b7280",
                }}
              >
                {rowTiming.status} · {rowTiming.time} · {rowTiming.pct.toFixed(0)}%
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#374151",
                  }}
                >
                  #
                </span>

                <input
  type="text"
  inputMode="numeric"
  pattern="[0-9]*"
  value={player.number}
  onClick={(e) => e.stopPropagation()}
  onFocus={(e) => {
    e.stopPropagation();
  }}
  onChange={(e) => updatePlayerNumber(team, player.name, e.target.value)}
  onBlur={() => {
    if (team === "A") {
      setTeamASquad((prev) =>
        prev.map((p) =>
          p.name === player.name
            ? { ...p, number: p.number === "" ? actualIndex + 1 : p.number }
            : p
        )
      );
    } else {
      setTeamBSquad((prev) =>
        prev.map((p) =>
          p.name === player.name
            ? { ...p, number: p.number === "" ? actualIndex + 1 : p.number }
            : p
        )
      );
    }
  }}
  style={{
    width: 28,
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "1px 3px",
    fontSize: 10,
    fontWeight: 700,
    textAlign: "center",
    background: "#fff",
  }}
  title="Shirt number"
/>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePlayerInjured(team, player.number);
                  }}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: "3px 5px",
                    fontSize: 10,
                    fontWeight: 700,
                    background: player.injured ? "#fee2e2" : "#f1f5f9",
                    color: player.injured ? "#b91c1c" : "#334155",
                    cursor: "pointer",
                  }}
                  title={player.injured ? "Mark as fit" : "Mark as injured"}
                >
                  {player.injured ? "Inj" : "Fit"}
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPlayerToRemove({
                      team,
                      number: player.number,
                      name: player.name,
                    });
                  }}
                  style={{
                    border: "1px solid #fecaca",
                    borderRadius: 8,
                    padding: "3px 5px",
                    background: "#fff5f5",
                    color: "#b91c1c",
                    fontSize: 10,
                    cursor: "pointer",
                    marginLeft: 8,
                  }}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
        {onField.map((player, index) => renderPlayerRow(player, index))}

        {subs.length > 0 && (
          <div
            style={{
              margin: "2px 0 4px",
              padding: "6px 0 0",
              borderTop: "2px solid #cbd5e1",
              textAlign: "center",
              fontSize: 10,
              fontWeight: 800,
              color: "#64748b",
              letterSpacing: "0.06em",
            }}
          >
            SUBS
          </div>
        )}

        {subs.map((player, subIndex) =>
          renderPlayerRow(player, activeTeamSize + subIndex)
        )}
      </div>
    );
  };

  const renderYellowCards = (team) => {
    const cards = ycTimers.filter((t) => t.team === team);
    if (!cards.length) return null;

    return (
      <div style={{ display: "grid", gap: 6, marginBottom: 10 }}>
        {cards.map((t) => {
          const rem = getYcRemaining(t);
          const expired = rem === 0;

          return (
            <div
              key={t.id}
              style={{
                display: "grid",
                gridTemplateColumns: "16px minmax(0, 1fr) 24px",
                alignItems: "center",
                gap: 6,
                background: expired ? "#fff1f1" : "#fff8db",
                border: expired ? "1px solid #f0b3b3" : "1px solid #e6d98e",
                borderRadius: 6,
                padding: "6px 6px",
                fontSize: 12,
                width: "100%",
                minWidth: 0,
                boxSizing: "border-box",
              }}
            >
              <div
                title="Yellow Card"
                style={{
                  fontSize: 12,
                  lineHeight: 1,
                  textAlign: "center",
                }}
              >
                🟨
              </div>

              <div
                style={{
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 12,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {shortPlayerName(t.player)}
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    marginTop: 2,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: 700,
                      fontSize: 12,
                      lineHeight: 1,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatClock(rem)}
                  </span>

                  {expired && (
                    <span
                      title="Expired"
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        padding: "1px 4px",
                        borderRadius: 4,
                        background: "#fce7e7",
                        border: "1px solid #f0b3b3",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    >
                      Exp
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => clearYcTimer(t.id)}
                title="Clear"
                aria-label="Clear yellow card timer"
                style={{
                  width: 24,
                  height: 24,
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  padding: 0,
                  lineHeight: 1,
                  fontSize: 12,
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                ✖
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  /* ------------------------------------------------------------------------
     EFFECTS: SPLASH SCREEN
     ------------------------------------------------------------------------ */

  useEffect(() => {
    const timer1 = setTimeout(() => setFadeOut(true), 3000);
    const timer2 = setTimeout(() => setShowSplash(false), 4000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  /* ------------------------------------------------------------------------
     EFFECTS: BASIC CLOCK PERSISTENCE
     ------------------------------------------------------------------------ */

  useEffect(() => {
    localStorage.setItem("seconds", String(seconds));
    localStorage.setItem("running", running ? "true" : "false");
  }, [seconds, running]);

  /* ------------------------------------------------------------------------
     EFFECTS: MAIN MATCH PERSISTENCE
     ------------------------------------------------------------------------ */

  useEffect(() => {
    localStorage.setItem("teamA", teamA);
    localStorage.setItem("teamB", teamB);
    localStorage.setItem("scoreA", String(scoreA));
    localStorage.setItem("scoreB", String(scoreB));
    localStorage.setItem("events", JSON.stringify(events));
    localStorage.setItem("currentHalf", String(currentHalf));
    localStorage.setItem("halfLength", String(halfLength));
    localStorage.setItem("ycDuration", String(ycDuration));
    localStorage.setItem("ycTimers", JSON.stringify(ycTimers));
    localStorage.setItem("halfTimeScore", JSON.stringify(halfTimeScore));
    localStorage.setItem("matchDate", matchDate);
    localStorage.setItem("koTime", koTime);
    localStorage.setItem("teamASquad", JSON.stringify(teamASquad));
    localStorage.setItem("teamBSquad", JSON.stringify(teamBSquad));
    localStorage.setItem("activeTeamSize", String(activeTeamSize));
  }, [
    teamA,
    teamB,
    scoreA,
    scoreB,
    events,
    currentHalf,
    halfLength,
    ycDuration,
    ycTimers,
    halfTimeScore,
    matchDate,
    koTime,
    teamASquad,
    teamBSquad,
    activeTeamSize,
  ]);

  /* ------------------------------------------------------------------------
     EFFECTS: PLAYER TIMER PERSISTENCE
     ------------------------------------------------------------------------ */

  useEffect(() => {
    localStorage.setItem("playerTimers", JSON.stringify(playerTimers));
  }, [playerTimers]);

  /* ------------------------------------------------------------------------
     EFFECTS: START TIMESTAMP PERSISTENCE
     ------------------------------------------------------------------------ */

  useEffect(() => {
    if (startTimestamp) {
      localStorage.setItem("startTimestamp", String(startTimestamp));
    }
  }, [startTimestamp]);

  /* ------------------------------------------------------------------------
     EFFECTS: MATCH CLOCK TICKING
     ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!running || !startTimestamp) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = Math.floor((now - startTimestamp) / 1000);
      setSeconds(elapsed);
    }, 500);

    return () => clearInterval(interval);
  }, [running, startTimestamp]);

  /* ------------------------------------------------------------------------
     EFFECTS: RECONCILE PLAYER TIMERS WHEN MATCH OR SQUADS CHANGE
     ------------------------------------------------------------------------ */

  useEffect(() => {
    const now = Date.now();
    const shouldRun = running && currentHalf > 0;

    setPlayerTimers((prev) => ({
      A: reconcileTeamTimers(
        teamASquad,
        prev?.A || {},
        now,
        shouldRun,
        activeTeamSize
      ),
      B: reconcileTeamTimers(
        teamBSquad,
        prev?.B || {},
        now,
        shouldRun,
        activeTeamSize
      ),
    }));
  }, [teamASquad, teamBSquad, activeTeamSize, running, currentHalf]);

  /* ------------------------------------------------------------------------
     SPLASH SCREEN RETURN
     ------------------------------------------------------------------------ */

  if (showSplash) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#800000",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          zIndex: 9999,
          opacity: fadeOut ? 0 : 1,
          transition: "opacity 1s ease-in-out",
        }}
      >
        {SPONSOR.enabled ? (
          <a
            href={SPONSOR.link}
            target="_blank"
            rel="noreferrer"
            aria-label={`Visit ${SPONSOR.name}`}
            title={`Visit ${SPONSOR.name}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <img
              src={SPONSOR.imgSrc}
              alt={SPONSOR.alt}
              style={{
                maxWidth: "70vw",
                width: 220,
                height: 220,
                objectFit: "contain",
                marginBottom: 12,
                background: "rgba(255,255,255,0.06)",
                borderRadius: 12,
                padding: 10,
              }}
            />
            <div style={{ fontSize: "1rem", fontWeight: 600, marginTop: 6 }}>
              Sponsored by {SPONSOR.name}
            </div>
          </a>
        ) : (
          <>
            <img
              src="/logo512.png"
              alt="Rugby Scoreboard Logo"
              style={{
                width: 150,
                height: 150,
                marginBottom: 12,
                filter: "drop-shadow(0 0 10px rgba(255,255,255,0.3))",
              }}
            />
            <h1 style={{ fontSize: "2rem", margin: 0 }}>Rugby Scoreboard</h1>

            <p
              style={{
                fontSize: "0.9rem",
                marginTop: 25,
                opacity: 0.95,
                color: "white",
                borderTop: "1px solid rgba(255,255,255,0.3)",
                paddingTop: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                flexWrap: "wrap",
                textAlign: "center",
              }}
              title="Want to advertise?"
            >
              <span style={{ fontSize: "1.2rem" }}>🏉</span>
              <em>
                ADVERTISE HERE – Support Our Team.{" "}
                <a
                  href="mailto:wilsonge@ymail.com"
                  style={{
                    color: "#fff",
                    textDecoration: "underline",
                    fontStyle: "normal",
                  }}
                >
                  Contact Me
                </a>
              </em>
            </p>
          </>
        )}
      </div>
    );
  }

  /* ------------------------------------------------------------------------
     MAIN APP RETURN
     ------------------------------------------------------------------------ */

  return (
    <div
      ref={scoreboardRef}
      style={{
        maxWidth: isExporting ? 560 : 700,
        margin: "auto",
        padding: isExporting ? 12 : 20,
        fontFamily: "Arial, sans-serif",
        background: "#fff",
      }}
    >
      {/* ====================================================================
          APP HEADER
          ==================================================================== */}

      <div style={{ textAlign: "center", marginBottom: isExporting ? 6 : 10 }}>
        <img
          src="/logo.png"
          alt="Rugby Scoreboard Logo"
          style={{
            width: isExporting ? 42 : 60,
            height: "auto",
            marginBottom: isExporting ? 4 : 6,
            borderRadius: 8,
          }}
        />
        <h1 style={{ margin: 0, fontSize: isExporting ? 22 : 32 }}>
          Rugby Scoreboard
        </h1>
      </div>

      {SPONSOR.enabled && (
        <div
          style={{
            margin: "6px 0 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "6px 8px",
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <span style={{ fontSize: 12, color: "#444" }}>Sponsored by</span>
          <a
            href={SPONSOR.link}
            target="_blank"
            rel="noreferrer"
            title={`Visit ${SPONSOR.name}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <img
              src={SPONSOR.imgSrc}
              alt={SPONSOR.alt}
              style={{
                width: 72,
                height: 36,
                objectFit: "contain",
                background: "rgba(0,0,0,0.03)",
                borderRadius: 6,
                padding: 4,
              }}
            />
            <strong style={{ fontSize: 13 }}>{SPONSOR.name}</strong>
          </a>
        </div>
      )}

      {!isExporting && (
        <div
          style={{
            textAlign: "center",
            fontSize: 12,
            color: "#555",
            marginBottom: 20,
          }}
        >
          © G. Wilson 2025 ·{" "}
          <a
            href="mailto:wilsonge@ymail.com"
            style={{ color: "#555", textDecoration: "underline" }}
          >
            Contact Me
          </a>{" "}
          ·{" "}
          <a
            href="https://instagram.com/rugby_scoreboard_app"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#555", textDecoration: "underline" }}
          >
            Instagram
          </a>
        </div>
      )}

      {/* ====================================================================
          MATCH DATE / KO SECTION
          ==================================================================== */}

      {currentHalf === 0 ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 12,
            marginBottom: 16,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ minWidth: 44, textAlign: "right" }}>Date</label>
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={matchDate}
              onChange={(e) => setMatchDate(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
              }}
            />
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ minWidth: 44, textAlign: "right" }}>KO</label>
            <input
              type="text"
              placeholder="HH:MM"
              value={koTime}
              onChange={(e) => setKoTime(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
              }}
            />
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>
            {[formatHumanDate(matchDate) || matchDate, koTime ? `KO ${koTime}` : null]
              .filter(Boolean)
              .join(" — ")}
          </div>
        </div>
      )}

      {/* ====================================================================
          TEAM MANAGEMENT
          ==================================================================== */}

      {!isExporting && (
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: 10,
            padding: 10,
            marginBottom: 15,
            background: "#fafafa",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div style={{ textAlign: "left" }}>
              <button
                onClick={() =>
                  setActiveManagementTeam((prev) => (prev === "A" ? null : "A"))
                }
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border:
                    activeManagementTeam === "A"
                      ? "2px solid #2563eb"
                      : "1px solid #d1d5db",
                  background: activeManagementTeam === "A" ? "#dbeafe" : "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                HOME
              </button>
            </div>

            <div
              style={{
                textAlign: "center",
                fontWeight: 800,
                fontSize: 15,
                color: "#334155",
                letterSpacing: "0.04em",
              }}
            >
              TEAM MANAGEMENT
            </div>

            <div style={{ textAlign: "right" }}>
              <button
                onClick={() =>
                  setActiveManagementTeam((prev) => (prev === "B" ? null : "B"))
                }
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border:
                    activeManagementTeam === "B"
                      ? "2px solid #2563eb"
                      : "1px solid #d1d5db",
                  background: activeManagementTeam === "B" ? "#dbeafe" : "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                AWAY
              </button>
            </div>
          </div>

          {activeManagementTeam === "A" && (
            <div
              style={{
                padding: 10,
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fff",
                marginTop: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                    color: "#475569",
                  }}
                >
                  {teamA} (Total {teamASquad.length})
                </div>
              </div>

              <textarea
                value={bulkA}
                onChange={(e) => setBulkA(e.target.value)}
                placeholder="Paste names (one per line or comma-separated)"
                style={{
                  width: "100%",
                  minHeight: 72,
                  padding: "8px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  boxSizing: "border-box",
                  fontSize: 14,
                  marginTop: 10,
                }}
              />

              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => addBulkToSquad("A")}
                  style={{
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Add Squad
                </button>

                {teamASquad.length > 0 && (
                  <button
                    onClick={() => setShowClearSquadConfirm("A")}
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 6,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Clear Squad
                  </button>
                )}
              </div>

              {selectedSwap?.team === "A" && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#2563eb",
                    marginTop: 8,
                    fontWeight: 600,
                  }}
                >
                  Selected: {teamASquad[selectedSwap.index]?.name} — tap another
                  player to swap.
                </div>
              )}

              <div style={{ marginTop: 10 }}>{renderSquadRows("A", teamASquad)}</div>
            </div>
          )}

          {activeManagementTeam === "B" && (
            <div
              style={{
                padding: 10,
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                background: "#fff",
                marginTop: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    fontWeight: 500,
                    fontSize: 13,
                    color: "#475569",
                  }}
                >
                  {teamB} (Total {teamBSquad.length})
                </div>
              </div>

              <textarea
                value={bulkB}
                onChange={(e) => setBulkB(e.target.value)}
                placeholder="Paste names (one per line or comma-separated)"
                style={{
                  width: "100%",
                  minHeight: 72,
                  padding: "8px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  boxSizing: "border-box",
                  fontSize: 14,
                  marginTop: 10,
                }}
              />

              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => addBulkToSquad("B")}
                  style={{
                    background: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: 6,
                    padding: "6px 10px",
                    cursor: "pointer",
                  }}
                >
                  Add Squad
                </button>

                {teamBSquad.length > 0 && (
                  <button
                    onClick={() => setShowClearSquadConfirm("B")}
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: 6,
                      padding: "6px 10px",
                      cursor: "pointer",
                    }}
                  >
                    Clear Squad
                  </button>
                )}
              </div>

              {selectedSwap?.team === "B" && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#2563eb",
                    marginTop: 8,
                    fontWeight: 600,
                  }}
                >
                  Selected: {teamBSquad[selectedSwap.index]?.name} — tap another
                  player to swap.
                </div>
              )}

              <div style={{ marginTop: 10 }}>{renderSquadRows("B", teamBSquad)}</div>
            </div>
          )}
        </div>
      )}

      {/* ====================================================================
          PRE-MATCH SETTINGS
          ==================================================================== */}

      {currentHalf === 0 && (
        <>
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <label>Select Half Length: </label>
            <select
              value={halfLength}
              onChange={(e) => setHalfLength(Number(e.target.value))}
            >
              {[10, 15, 20, 25, 30, 35, 40].map((m) => (
                <option key={m} value={m}>
                  {m} minutes
                </option>
              ))}
            </select>
          </div>

          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <label>Yellow Card Duration: </label>
            <select
              value={ycDuration}
              onChange={(e) => setYcDuration(Number(e.target.value))}
            >
              {[6, 7, 10].map((m) => (
                <option key={m} value={m}>
                  {m} minutes
                </option>
              ))}
            </select>
          </div>

          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <label>Team Size: </label>
            <select
              value={activeTeamSize}
              onChange={(e) => setActiveTeamSize(Number(e.target.value))}
            >
              {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map((n) => (
                <option key={n} value={n}>
                  {n} players
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {isExporting && (
        <div
          style={{
            textAlign: "center",
            marginBottom: 10,
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.12em",
            color: "#555",
          }}
        >
          FULL TIME
        </div>
      )}

      {/* ====================================================================
          SCORE PANELS
          ==================================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: isExporting ? 12 : 20,
          gap: isExporting ? 10 : 0,
        }}
      >
        <div
          style={{
            border: "2px solid black",
            borderRadius: 10,
            padding: 15,
            width: "48%",
            cursor: "pointer",
            textAlign: "center",
            backgroundColor: "#f0f8ff",
          }}
          onClick={() => openScoring("A")}
        >
          <div
            style={{
              width: "100%",
              height: 44,
              marginBottom: 8,
              padding: "0 6px",
              boxSizing: "border-box",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.15,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
            title={teamA}
          >
            {teamA}
          </div>

          {!isExporting && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditingTeam("A");
                setEditingTeamName(teamA);
              }}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                marginBottom: 8,
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Edit Name
            </button>
          )}

          <div
            style={{
              fontSize: isExporting ? 64 : 50,
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            {scoreA}
          </div>
        </div>

        <div
          style={{
            border: "2px solid black",
            borderRadius: 10,
            padding: 15,
            width: "48%",
            cursor: "pointer",
            textAlign: "center",
            backgroundColor: "#fff0f5",
          }}
          onClick={() => openScoring("B")}
        >
          <div
            style={{
              width: "100%",
              height: 44,
              marginBottom: 8,
              padding: "0 6px",
              boxSizing: "border-box",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
              fontSize: 15,
              fontWeight: 700,
              lineHeight: 1.15,
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
            title={teamB}
          >
            {teamB}
          </div>

          {!isExporting && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setEditingTeam("B");
                setEditingTeamName(teamB);
              }}
              style={{
                fontSize: 11,
                padding: "2px 8px",
                marginBottom: 8,
                borderRadius: 6,
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              Edit Name
            </button>
          )}

          <div
            style={{
              fontSize: isExporting ? 64 : 50,
              fontWeight: "bold",
              lineHeight: 1,
            }}
          >
            {scoreB}
          </div>
        </div>
      </div>

      {/* ====================================================================
          MATCH CLOCK / HALF CONTROLS
          ==================================================================== */}

      <div style={{ textAlign: "center", marginBottom: 20 }}>
        {halfTimeScore && currentHalf >= 1 && (
          <div style={{ textAlign: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>
              {halfTimeScore.a} - {halfTimeScore.b}
            </div>
            <div style={{ fontSize: 12, color: "#666", letterSpacing: "0.08em" }}>
              HT
            </div>
          </div>
        )}

        {!isExporting && (
          <div
            style={{
              fontSize: 36,
              fontWeight: "bold",
              marginBottom: 12,
              color: isOvertime ? "red" : "black",
            }}
          >
            ⏱ {formatClock(seconds)}
          </div>
        )}

        {!isExporting && currentHalf === 0 && (
          <div style={timerButtonRowStyle}>
            <button onClick={() => startHalf(1)} style={startHalfButtonStyle}>
              ▶ Start 1st Half
            </button>
          </div>
        )}

        {!isExporting && currentHalf === 1 && (
          <div style={timerButtonRowStyle}>
            <button onClick={endHalf} style={endHalfButtonStyle}>
              ■ End 1st Half
            </button>

            <button onClick={togglePause} style={pauseResumeButtonStyle}>
              {running ? "⏸ Pause" : "▶ Resume"}
            </button>

            {!running && seconds >= halfLength * 60 && (
              <button onClick={() => startHalf(2)} style={startHalfButtonStyle}>
                ▶ Start 2nd Half
              </button>
            )}
          </div>
        )}

        {!isExporting && currentHalf === 2 && (
          <div style={timerButtonRowStyle}>
            <button onClick={endHalf} style={endHalfButtonStyle}>
              ■ End 2nd Half
            </button>

            <button onClick={togglePause} style={pauseResumeButtonStyle}>
              {running ? "⏸ Pause" : "▶ Resume"}
            </button>
          </div>
        )}
      </div>

      {/* ====================================================================
          ADD EVENT MODAL
          ==================================================================== */}

      {scoringFor &&
        ReactDOM.createPortal(
          <div
            onClick={resetScoringState}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10000,
              background: "rgba(0,0,0,0.35)",
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              paddingTop: "max(6vh, calc(env(safe-area-inset-top, 0px) + 16px))",
              paddingLeft: 12,
              paddingRight: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 720,
                border: "2px solid #007bff",
                borderRadius: 12,
                background: "#e6f0ff",
                padding: 12,
                boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  Add event for {scoringFor === "A" ? teamA : teamB}
                </div>
                <button onClick={resetScoringState} style={{ fontWeight: 700 }}>
                  ✕
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 10,
                  marginTop: 8,
                }}
              >
                {Object.keys(SCORE_TYPES).map((k) => {
                  const selected = scoringType === k;

                  return (
                    <div
                      key={k}
                      onClick={() => {
                        setScoringType(k);
                        if (TYPE_NO_PLAYER.has(k)) {
                          commitScoreDirect(k, "");
                        }
                      }}
                      style={{
                        padding: "12px 10px",
                        border: `2px solid ${selected ? "#2563eb" : "#d1d5db"}`,
                        borderRadius: 10,
                        background: selected ? "#dbeafe" : "#f8fafc",
                        textAlign: "center",
                        cursor: "pointer",
                        fontSize: 14,
                        fontWeight: 600,
                        userSelect: "none",
                        transition: "border-color .15s, background .15s",
                      }}
                    >
                      {SCORE_TYPES[k].label}
                    </div>
                  );
                })}
              </div>

              {!TYPE_NO_PLAYER.has(scoringType) && (
                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <input
                    placeholder="Player Name"
                    value={scoringPlayer}
                    onChange={(e) => setScoringPlayer(e.target.value)}
                    style={{ fontSize: 16, padding: 4, flex: "1 1 200px" }}
                  />

                  {(() => {
                    const squad = scoringFor === "A" ? teamASquad : teamBSquad;
                    if (!squad || squad.length === 0) return null;

                    return (
                      <div style={{ width: "100%", marginTop: 10 }}>
                        <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                          Tap a name to add
                        </div>

                        <div
                          style={{
                            maxHeight: 160,
                            overflowY: "auto",
                            border: "1px solid #d1d5db",
                            borderRadius: 8,
                            background: "#fff",
                          }}
                        >
                          {squad.map((player, index) => {
                            const selected = player.name === selectedSquadName;

                            return (
                              <button
                                key={`${player.number}-${player.name}`}
                                type="button"
                                onClick={() => pickSquadName(player)}
                                style={{
                                  width: "100%",
                                  textAlign: "left",
                                  padding: "10px",
                                  border: "none",
                                  borderBottom: "1px solid #eee",
                                  background: selected
                                    ? "rgba(0,123,255,0.12)"
                                    : "transparent",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  readOnly
                                  style={{ width: 16, height: 16 }}
                                />
                                <div
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    width: "100%",
    minWidth: 0,
  }}
>
  <span
    style={{
      fontSize: 14,
      fontWeight: selected ? 700 : 500,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {index + 1}. {player.name}
  </span>

  <span
  style={{
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    color: "#475569",
    background: "#f1f5f9",
    border: "1px solid #cbd5e1",
    borderRadius: 999,
    padding: "2px 8px",
  }}
>
  #{player.number || "—"}
</span>
</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={() => {
                    commitScoreDirect(scoringType, scoringPlayer);
                    setSelectedSquadName("");
                    setScoringPlayer("");
                  }}
                >
                  Add
                </button>

                <button onClick={resetScoringState}>Cancel</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ====================================================================
          EVENTS BOARD
          ==================================================================== */}

      {isExporting && (
        <div
          style={{
            height: 1,
            background: "#e5e7eb",
            margin: "10px 0 14px",
          }}
        />
      )}

      <div style={{ marginTop: 20 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 72px 1fr",
            gap: 6,
            alignItems: "start",
            marginBottom: 10,
          }}
        >
          <div style={{ textAlign: "center", fontWeight: 700 }}>{teamA} Events</div>
          <div></div>
          <div style={{ textAlign: "center", fontWeight: 700 }}>{teamB} Events</div>
        </div>

        {(ycTimers.some((t) => t.team === "A") ||
          ycTimers.some((t) => t.team === "B")) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 8,
              rowGap: 6,
              alignItems: "start",
              marginBottom: 12,
            }}
          >
            <div style={{ minWidth: 0 }}>{renderYellowCards("A")}</div>
            <div style={{ minWidth: 0 }}>{renderYellowCards("B")}</div>
          </div>
        )}

        {EVENT_SECTION_ORDER.map((section) => {
          const left = groupedEventEntries("A", section.types);
          const right = groupedEventEntries("B", section.types);

          if (!left.length && !right.length) return null;

          return (
            <div
              key={section.key}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 72px 1fr",
                gridTemplateRows: "auto auto",
                columnGap: 6,
                rowGap: 4,
                alignItems: "start",
                marginBottom: 12,
              }}
            >
              <div></div>

              <div
                style={{
                  textAlign: "center",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  paddingTop: 2,
                  borderTop: "1px solid #444",
                  fontSize: 11,
                  lineHeight: 1.1,
                }}
              >
                {section.label}
              </div>

              <div></div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  lineHeight: 1.2,
                  fontSize: 12,
                }}
              >
                <div style={{ textAlign: "left", maxWidth: "100%" }}>
                  {left.map((group) => (
                    <div
                      key={group.key}
                      onClick={() => setSelectedGroupedEvents(group.events)}
                      style={{
                        cursor: "pointer",
                        padding: "1px 0",
                        wordBreak: "break-word",
                      }}
                      title="Tap to choose an event"
                    >
                      {group.type === "yellow_card" ? "🟨 " : ""}
                      {group.type === "red_card" ? "🟥 " : ""}
                      {group.player} (
                      {group.events.map((e) => e.timeLabel || `${e.timeMin}′`).join(", ")}
                      )
                    </div>
                  ))}
                </div>
              </div>

              <div></div>

              <div style={{ textAlign: "left", lineHeight: 1.2, fontSize: 12 }}>
                {right.map((group) => (
                  <div
                    key={group.key}
                    onClick={() => setSelectedGroupedEvents(group.events)}
                    style={{ cursor: "pointer", padding: "1px 0" }}
                    title="Tap to choose an event"
                  >
                    {group.type === "yellow_card" ? "🟨 " : ""}
                    {group.type === "red_card" ? "🟥 " : ""}
                    {group.player} (
                    {group.events.map((e) => e.timeLabel || `${e.timeMin}′`).join(", ")})
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ====================================================================
          MATCH ACTION BUTTONS
          ==================================================================== */}

      {!isExporting && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button onClick={exportImage}>Match Summary</button>

            <button onClick={() => setShowConfirm(true)}>New Match</button>
          </div>

          <div style={{ marginTop: 10 }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowPlayingSummary(true);
              }}
            >
              Playing Time Report
            </button>
          </div>
        </div>
      )}

      {/* ====================================================================
          NEW MATCH CONFIRM MODAL
          ==================================================================== */}

      {showConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: "90%",
              maxWidth: 360,
              textAlign: "center",
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginBottom: 10 }}>Start a new match?</h3>
            <p style={{ fontSize: 14, color: "#555", marginBottom: 20 }}>
              This will clear the current match data.
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  padding: "8px 14px",
                  background: "#ddd",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  newMatch();
                  setShowConfirm(false);
                }}
                style={{
                  padding: "8px 14px",
                  background: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Yes, Start New Match
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          REMOVE PLAYER CONFIRM MODAL
          ==================================================================== */}

      {playerToRemove && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={() => setPlayerToRemove(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: "90%",
              maxWidth: 320,
              textAlign: "center",
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginBottom: 10 }}>Remove player?</h3>

            <p style={{ fontSize: 14, color: "#555", marginBottom: 20 }}>
              {playerToRemove.name} will be removed from the squad.
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
              <button
                onClick={() => setPlayerToRemove(null)}
                style={{
                  padding: "8px 14px",
                  background: "#ddd",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  removeFromSquad(playerToRemove.team, playerToRemove.number);
                  setPlayerToRemove(null);
                }}
                style={{
                  padding: "8px 14px",
                  background: "#d32f2f",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          CLEAR SQUAD CONFIRM MODAL
          ==================================================================== */}

      {showClearSquadConfirm && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: 20,
              borderRadius: 10,
              maxWidth: 320,
              width: "90%",
              textAlign: "center",
            }}
          >
            <p style={{ fontWeight: 600, marginBottom: 16 }}>
              Clear squad for Team {showClearSquadConfirm === "A" ? "Home" : "Away"}?
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => {
                  clearSquad(showClearSquadConfirm);
                  setShowClearSquadConfirm(null);
                }}
              >
                Yes, clear
              </button>

              <button onClick={() => setShowClearSquadConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          PLAYING SUMMARY MODAL
          ==================================================================== */}

      {showPlayingSummary && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 12,
          }}
          onClick={() => setShowPlayingSummary(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              width: "100%",
              maxWidth: 800,
              borderRadius: 12,
              padding: 16,
              maxHeight: "85vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <h3 style={{ margin: 0 }}>Playing Time Summary</h3>

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={exportPlayingSummaryImage}>Save Image</button>
                <button onClick={() => setShowPlayingSummary(false)}>Close</button>
              </div>
            </div>

            <div
              ref={playingSummaryRef}
              style={{
                background: "#fff",
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 10,
              }}
            >
              <div style={{ textAlign: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 24, fontWeight: 800 }}>
                  Playing Time Summary
                </div>
                <div style={{ fontSize: 14, color: "#666", marginTop: 4 }}>
                  {teamA} v {teamB}
                </div>
                <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
                  {matchDate} {koTime ? `• KO ${koTime}` : ""}
                </div>
              </div>

              {[
                ["A", teamA],
                ["B", teamB],
              ].map(([teamKey, teamLabel]) => {
                const rows = getPlayingSummary(teamKey);

                return (
                  <div key={teamKey} style={{ marginBottom: 20 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        marginBottom: 8,
                        textAlign: "center",
                        borderTop: "1px solid #e5e7eb",
                        paddingTop: 8,
                      }}
                    >
                      {teamLabel}
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      {rows.map((player, index) => (
                        <div
  key={`${teamKey}-${player.number}-${player.name}`}
  style={{
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 72px 54px",
    gap: 8,
    alignItems: "center",
    padding: "8px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
  }}
>
  <div
    style={{
      minWidth: 0,
      fontWeight: 600,
      lineHeight: 1.2,
      wordBreak: "break-word",
      overflowWrap: "anywhere",
    }}
  >
    {index + 1}. {player.name}
    {player.injured && (
      <span style={{ marginLeft: 6, color: "#b91c1c", fontWeight: 700 }}>
        (Injured)
      </span>
    )}
  </div>

  <div
    style={{
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      fontWeight: 700,
      whiteSpace: "nowrap",
      fontSize: 13,
    }}
  >
    {player.time}
  </div>

  <div
    style={{
      textAlign: "right",
      fontWeight: 700,
      whiteSpace: "nowrap",
      fontSize: 13,
      color: player.percentage < 50 ? "#dc2626" : "#16a34a",
    }}
  >
    {player.percentage.toFixed(0)}%
  </div>
</div>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div
                style={{
                  textAlign: "center",
                  marginTop: 10,
                  fontSize: 12,
                  color: "#666",
                }}
              >
                Percentage is based on scheduled match length.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          GROUPED EVENT PICKER MODAL
          ==================================================================== */}

      {selectedGroupedEvents && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 12,
          }}
          onClick={() => setSelectedGroupedEvents(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: "100%",
              maxWidth: 360,
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>Choose Event</h3>

            <div style={{ display: "grid", gap: 8 }}>
              {selectedGroupedEvents.map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => {
                    setSelectedGroupedEvents(null);
                    setSelectedEvent(ev);
                    setEditingEventId(ev.id);
                    setEditingPlayerName(ev.player || "");
                  }}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  {ev.type === "yellow_card" ? "🟨 " : ""}
                  {ev.type === "red_card" ? "🟥 " : ""}
                  {ev.player} ({ev.timeLabel || `${ev.timeMin}′`})
                </button>
              ))}
            </div>

            <div style={{ textAlign: "center", marginTop: 14 }}>
              <button onClick={() => setSelectedGroupedEvents(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          TEAM NAME EDIT MODAL
          ==================================================================== */}

      {editingTeam && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 12,
          }}
          onClick={() => setEditingTeam(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: "100%",
              maxWidth: 320,
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              Edit {editingTeam === "A" ? "Home" : "Away"} Team Name
            </h3>

            <input
              type="text"
              value={editingTeamName}
              onChange={(e) => setEditingTeamName(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 10px",
                border: "1px solid #d1d5db",
                borderRadius: 6,
                boxSizing: "border-box",
                marginBottom: 12,
              }}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={() => {
                  if (editingTeam === "A") setTeamA(editingTeamName);
                  else setTeamB(editingTeamName);
                  setEditingTeam(null);
                }}
              >
                Save
              </button>

              <button onClick={() => setEditingTeam(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ====================================================================
          HIDDEN EXPORT CARD
          ==================================================================== */}

      <div
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          pointerEvents: "none",
          opacity: 0,
        }}
      >
        <div
          ref={exportCardRef}
          style={{
            width: 1080,
            height: 1080,
            background: "#f3f6fb",
            padding: 70,
            boxSizing: "border-box",
            fontFamily: "Arial, sans-serif",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#ffffff",
              borderRadius: 26,
              padding: "50px 60px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <img
              src="/logo.png"
              alt=""
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 420,
                opacity: 0.05,
                pointerEvents: "none",
              }}
            />

            <div style={{ textAlign: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 30, fontWeight: 800 }}>
                Rugby Scoreboard
              </div>

              <div
                style={{
                  fontSize: 14,
                  color: "#64748b",
                  marginTop: 4,
                  fontWeight: 500,
                }}
              >
                {matchDate} • KO {koTime}
              </div>

              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#666",
                  marginTop: 6,
                }}
              >
                FULL TIME
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                marginBottom: 22,
                background: "#dae9f9",
                padding: "14px 18px",
                borderRadius: 14,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 60px 1fr",
                  alignItems: "center",
                  marginBottom: 8,
                  fontWeight: 800,
                  fontSize: 30,
                }}
              >
                <div style={{ textAlign: "right", paddingRight: 10 }}>{teamA}</div>

                <div
                  style={{
                    textAlign: "center",
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#444",
                  }}
                >
                  v
                </div>

                <div style={{ textAlign: "left", paddingLeft: 10 }}>{teamB}</div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 60px 1fr",
                  alignItems: "center",
                  fontSize: 58,
                  fontWeight: 900,
                  lineHeight: 1,
                }}
              >
                <div style={{ textAlign: "right", paddingRight: 10 }}>{scoreA}</div>

                <div
                  style={{
                    textAlign: "center",
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#444",
                  }}
                >
                  -
                </div>

                <div style={{ textAlign: "left", paddingLeft: 10 }}>{scoreB}</div>
              </div>

              {halfTimeScore && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 18,
                    color: "#666",
                    fontWeight: 700,
                  }}
                >
                  HT {halfTimeScore.a} - {halfTimeScore.b}
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: 14,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                overflow: "hidden",
              }}
            >
              {EXPORT_SECTIONS.map((section) => {
                const leftItems = exportGroupedItems("A", section.types);
                const rightItems = exportGroupedItems("B", section.types);

                if (!leftItems.length && !rightItems.length) return null;

                return (
                  <div
                    key={section.label}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 170px 1fr",
                      gridTemplateRows: "auto auto",
                      columnGap: 24,
                      rowGap: 8,
                      alignItems: "start",
                      marginBottom: 12,
                    }}
                  >
                    <div></div>

                    <div
                      style={{
                        textAlign: "center",
                        fontSize: 13,
                        letterSpacing: "0.12em",
                        fontWeight: 800,
                        color: "#475569",
                        textTransform: "uppercase",
                        paddingTop: 8,
                        borderTop: "1px solid #d1d5db",
                      }}
                    >
                      {section.label}
                    </div>

                    <div></div>

                    <div
                      style={{
                        textAlign: "right",
                        fontSize: 15,
                        lineHeight: 1.35,
                      }}
                    >
                      {leftItems.length > 0 && (
                        <span>
                          {leftItems.map((item, i) => (
                            <span key={item.player}>
                              <strong>
                                {section.label === "Yellow Cards" && "🟨 "}
                                {section.label === "Red Cards" && "🟥 "}
                                {item.player}
                              </strong>{" "}
                              ({item.items.map((m) => m.label).join(", ")})
                              {i < leftItems.length - 1 ? "; " : ""}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>

                    <div></div>

                    <div
                      style={{
                        textAlign: "left",
                        fontSize: 15,
                        lineHeight: 1.35,
                      }}
                    >
                      {rightItems.length > 0 && (
                        <span>
                          {rightItems.map((item, i) => (
                            <span key={item.player}>
                              <strong>
                                {section.label === "Yellow Cards" && "🟨 "}
                                {section.label === "Red Cards" && "🟥 "}
                                {item.player}
                              </strong>{" "}
                              ({item.items.map((m) => m.label).join(", ")})
                              {i < rightItems.length - 1 ? "; " : ""}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              style={{
                textAlign: "center",
                marginTop: 20,
                fontSize: 14,
                color: "#666",
                fontWeight: 700,
              }}
            >
              <div style={{ marginBottom: 8 }}>Sponsored by {SPONSOR.name}</div>

              <img
                src={SPONSOR.imgSrc}
                alt={SPONSOR.alt}
                style={{
                  width: 120,
                  height: 60,
                  objectFit: "contain",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ====================================================================
          EDIT EVENT MODAL
          ==================================================================== */}

      {selectedEvent && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 12,
          }}
          onClick={() => {
            setSelectedEvent(null);
            cancelEditEvent();
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              width: "100%",
              maxWidth: 360,
              boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: 10 }}>Edit Event</h3>

            <div style={{ fontSize: 14, color: "#555", marginBottom: 12 }}>
              {selectedEvent.typeLabel} · {selectedEvent.timeMin}′
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
                Player
              </label>
              <input
                type="text"
                value={
                  editingEventId === selectedEvent.id
                    ? editingPlayerName
                    : selectedEvent.player
                }
                onChange={(e) => {
                  if (editingEventId !== selectedEvent.id) {
                    setEditingEventId(selectedEvent.id);
                  }
                  setEditingPlayerName(e.target.value);
                }}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={() => {
                  if (editingEventId !== selectedEvent.id) {
                    setEditingEventId(selectedEvent.id);
                    setEditingPlayerName(selectedEvent.player || "");
                  }
                  saveEditEvent();
                  setSelectedEvent(null);
                }}
              >
                Save
              </button>

              <button
                onClick={() => {
                  undoEvent(selectedEvent.id);
                  setSelectedEvent(null);
                  cancelEditEvent();
                }}
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                }}
              >
                Delete
              </button>

              <button
                onClick={() => {
                  setSelectedEvent(null);
                  cancelEditEvent();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}